import { Fragment, useMemo } from "react";
import { Quote as QuoteIcon } from "lucide-react";

import { useDailyQuote } from "@/hooks/use-daily-quote";
import { splitQuoteForDisplay } from "@/lib/daily-quote";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface DailyQuoteProps {
  className?: string;
}

/**
 * The daily quote — one quote per calendar day.
 *
 * It is a direct child of the hero's flex row, so it takes two shapes:
 *
 *   - **mobile** (`flex-col`): stacked under the CTA row, separated by a
 *     hairline. This is the original placement.
 *   - **desktop** (`lg:flex-row`): a narrow rail beside the hero's content,
 *     aligned to its bottom edge and pushed right by the parent's `ml-auto`.
 *     The top rule rotates to a left rail and the quote mark moves above the
 *     text, so the column reads as an editorial sidebar rather than a second
 *     card.
 *
 * There is no offline fallback (dwyl/quotes is the only source), so a first
 * visit has nothing to render until the dataset lands. The skeleton below
 * mirrors the real block's box — two lines plus the attribution — so the hero
 * does not grow when the text replaces it.
 */
const DailyQuote = ({ className }: DailyQuoteProps) => {
  const { t } = useTranslation();
  const { quote, isLoading } = useDailyQuote();
  const lines = useMemo(
    () => (quote ? splitQuoteForDisplay(quote.text) : []),
    [quote],
  );

  if (isLoading) {
    return (
      <div
        aria-hidden="true"
        className={cn(
          "border-t border-stone-300/50 pt-4 dark:border-stone-700/60",
          "lg:ml-auto lg:w-64 lg:shrink-0 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0",
          className,
        )}
      >
        <div className="flex animate-pulse gap-3">
          <div className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded bg-stone-200 dark:bg-stone-700 lg:hidden" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3.5 w-full rounded bg-stone-200 dark:bg-stone-700" />
            <div className="h-3.5 w-4/5 rounded bg-stone-200 dark:bg-stone-700" />
            <div className="h-2.5 w-20 rounded bg-stone-200/70 dark:bg-stone-700/70" />
          </div>
        </div>
      </div>
    );
  }

  // No quote and not loading means the fetch failed — render nothing rather
  // than an empty rail.
  if (!quote) return null;

  return (
    <figure
      aria-label={t("home.quote.label", "Quote of the day")}
      className={cn(
        // Mobile: stacked, hairline above. Spacing comes from the parent's gap.
        "border-t border-stone-300/50 pt-4 dark:border-stone-700/60",
        // Desktop: right rail — rule rotates to the left, no top offset, and the
        // text right-aligns to the hero's padding edge as a clean vertical line.
        "lg:ml-auto lg:w-64 lg:shrink-0 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0 lg:text-right",
        className,
      )}
    >
      {/* lg:block lifts the mark out of the row and sets it above the text,
          where the figure's text-right carries it to the hero's padding edge.
          Inline beside right-aligned text it would strand itself mid-column. */}
      <div className="flex items-start gap-3 lg:block">
        <QuoteIcon
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500/80 dark:text-stone-400 lg:mb-2 lg:mt-0 lg:h-4 lg:w-4"
          aria-hidden="true"
        />
        <blockquote className="min-w-0">
          <p
            className={cn(
              "font-display text-sm font-medium leading-relaxed text-balance",
              "text-stone-600 dark:text-stone-300 sm:text-[0.9375rem]",
            )}
          >
            {lines.map((line, i) => (
              <Fragment key={`${i}-${line}`}>
                {i > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </p>
          <footer className="mt-1 font-sans text-[11px] font-medium uppercase tracking-wider text-stone-500 dark:text-stone-400">
            {quote.author}
          </footer>
        </blockquote>
      </div>
    </figure>
  );
};

export { DailyQuote };
export type { DailyQuoteProps };
